import { MockedAuthManager } from "../../contexts/AuthManager";
import { MockedProvider } from "@apollo/client/testing/react";
import React, { useMemo } from "react";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { ContractSignatureStatus, ProfileType } from "../../__generated__/graphql";
import { GET_CONTRACT } from "../../graphql/core";
import {
    buildAsset,
    buildClientInfo,
    buildClientProfile,
    buildContract,
    buildMissionInfo,
    buildUser,
} from "../factories";
import type { Meta, StoryObj } from "@storybook/react";
import { MockLink } from "@apollo/client/testing";
import { Button, View } from "react-native";
import Callout from "../../ui_atoms/Callout";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";
import ContractPreviewScreen from "../../screens/user/ContractPreviewScreen";
import { GET_ASSET } from "../../graphql/asset";
import { StackScreenProps } from "@react-navigation/stack";
import { StoryStackNavigatorContainer } from "../display-helpers";

type StoryStackParams = {
    Home: undefined;
    ContractPreviewScreen: { contractId: string };
    MissionSelectSignerScreen: { missionId: string; showCurrentProfile?: boolean };
    ContractSigningScreen: { contractId: string };
};

const HomeFakeScreen: React.FC<StackScreenProps<StoryStackParams, "Home">> = ({ navigation }) => {
    return (
        <View>
            <Callout markdown="Home screen" />
            <Button
                title="Back to preview"
                onPress={() => {
                    navigation.reset({
                        index: 0,
                        routes: [
                            {
                                name: "ContractPreviewScreen",
                                params: { contractId: "test-contract-need_self_sign" },
                            },
                        ],
                    });
                }}
            />
        </View>
    );
};

const DelegateSigner: React.FC<StackScreenProps<StoryStackParams, "MissionSelectSignerScreen">> = ({
    navigation,
    route,
}) => {
    return (
        <View>
            <Callout markdown={`Select Signer: ${route.params.missionId}`} />
            <Button
                title="Back to preview"
                onPress={() => {
                    navigation.reset({
                        index: 0,
                        routes: [
                            {
                                name: "ContractPreviewScreen",
                                params: { contractId: "test-contract-need_self_sign" },
                            },
                        ],
                    });
                }}
            />
        </View>
    );
};

const SignScreen: React.FC<StackScreenProps<StoryStackParams, "ContractSigningScreen">> = ({
    navigation,
    route,
}) => {
    return (
        <View>
            <Callout markdown={`Sign: ${route.params.contractId}`} />
            <Button
                title="Back to preview"
                onPress={() => {
                    navigation.reset({
                        index: 0,
                        routes: [
                            {
                                name: "ContractPreviewScreen",
                                params: { contractId: "test-contract-need_self_sign" },
                            },
                        ],
                    });
                }}
            />
        </View>
    );
};

const Stack = createNativeStackNavigator<StoryStackParams>();

const StoryComponent = ({
    profileType,
    canSign = true,
}: {
    profileType: ProfileType;
    canSign: boolean;
}) => {
    const user = useMemo(
        () =>
            buildUser({
                clientInfos: [buildClientInfo({ id: "client" })],
                clientProfiles: [
                    buildClientProfile({
                        id: "client",
                        canSignContracts: canSign,
                    }),
                ],
            }),
        [canSign],
    );
    const mock = useMemo(
        () =>
            Object.values(ContractSignatureStatus)
                .reduce((acc, state) => {
                    const contractId = `test-contract-${state}`;
                    acc.push({
                        request: {
                            query: GET_CONTRACT,
                            variables: {
                                id: contractId,
                            },
                        },
                        result: () => {
                            return {
                                data: {
                                    getContract: {
                                        ...buildContract({
                                            id: contractId,
                                            signatureStatus: state,
                                            contractEffectiveAsset: buildAsset(),
                                        }),
                                        ...{ missionInfo: buildMissionInfo() },
                                    },
                                },
                            };
                        },
                    });
                    return acc;
                }, [] as MockLink.MockedResponse[])
                .concat([
                    {
                        request: {
                            query: GET_ASSET,
                            variables: () => true,
                        },
                        result: () => {
                            return {
                                data: {
                                    getAsset: buildAsset(),
                                },
                            };
                        },
                    },
                ]),
        [],
    );

    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={profileType} profileId={"client"}>
                <MockedProvider mocks={mock}>
                    <StoryStackNavigatorContainer>
                        <Stack.Navigator initialRouteName="ContractPreviewScreen">
                            <Stack.Screen
                                name="ContractPreviewScreen"
                                component={ContractPreviewScreen}
                                initialParams={{ contractId: `test-contract-need_self_sign` }}
                                options={{
                                    gestureEnabled: false,
                                    headerShown: true,
                                    title: "",
                                    headerBackTitle: "Back",
                                    headerBackButtonDisplayMode: "default",
                                }}
                            />
                            <Stack.Screen name="Home" component={HomeFakeScreen} />
                            <Stack.Screen
                                name="MissionSelectSignerScreen"
                                component={DelegateSigner}
                            />
                            <Stack.Screen name="ContractSigningScreen" component={SignScreen} />
                        </Stack.Navigator>
                    </StoryStackNavigatorContainer>
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Screens/Contract preview",
    component: StoryComponent,
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Client: Story = {
    args: {
        profileType: ProfileType.Client,
        canSign: true,
    },
};

export const ClientNoSign: Story = {
    args: {
        profileType: ProfileType.Client,
        canSign: false,
    },
};

export const Worker: Story = {
    args: {
        profileType: ProfileType.Worker,
        canSign: true,
    },
};
